import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, ChevronRight, FileDown, FileSpreadsheet } from "lucide-react";
import { download } from "@/components/gst";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SearchBox, Empty, TableWrap, th, td, tr } from "@/components/common";
import { PartyDialog } from "@/components/party";

export default function Parties() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const fin = ["admin", "accounts"].includes(role);
  const [dueOnly, setDueOnly] = useState(false);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/parties").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => items.filter((p) => (!dueOnly || p.pending > 0.5) && `${p.name} ${p.gstin} ${p.contact_person} ${p.city}`.toLowerCase().includes(q.toLowerCase())), [items, q, dueOnly]);
  const totalPending = items.reduce((a, p) => a + (p.pending > 0 ? p.pending : 0), 0);
  const exp = (fmt) => download(`/party-pending/export?fmt=${fmt}`, {}, `Party_Pending.${fmt}`);
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/parties/${p.id}`); toast.success("Party deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Party Master" subtitle={`${items.length} saved parties · consignors & consignees`}>
        {fin && <><Button variant="outline" data-testid="party-pending-pdf" onClick={() => exp("pdf")}><FileDown className="mr-1 h-4 w-4" />Pending PDF</Button>
          <Button variant="outline" data-testid="party-pending-xlsx" onClick={() => exp("xlsx")}><FileSpreadsheet className="mr-1 h-4 w-4" />Pending Excel</Button></>}
        <Button data-testid="add-party-btn" onClick={() => { setEdit(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" />Add Party</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3"><SearchBox testid="party-search-input" value={q} onChange={setQ} placeholder="Search name, GSTIN, contact, city…" />
        {fin && <><label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="party-due-only" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} />Sirf pending wali party</label>
          <span className="ml-auto rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700" data-testid="party-total-pending">Total Pending: {inr(totalPending)}</span></>}
      </div>
      {rows.length === 0 && <Empty testid="parties-empty" />}
      {rows.length > 0 && (
        <TableWrap testid="parties-table">
          <table className="w-full text-sm">
            <thead><tr>{["#", "Party Name", "City / State", "GSTIN", "Contact", "Bookings", "Freight", ...(fin ? ["Pending Payment"] : []), ""].map((h, i) => <th key={i} className={`${th} ${i >= 5 && h ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={p.id} data-testid={`party-card-${p.id}`} onClick={() => role !== "operation" && navigate(`/parties/${p.id}`)} className={`${tr} cursor-pointer`}>
                  <td className={`${td} text-muted-foreground`}>{i + 1}</td>
                  <td className={`${td} font-semibold`}>{p.name}</td>
                  <td className={`${td} text-muted-foreground`}>{[p.city, p.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className={`${td} font-mono text-xs`}>{p.gstin || "—"}</td>
                  <td className={`${td} text-muted-foreground`}>{[p.contact_person, p.phone].filter(Boolean).join(" · ") || "—"}</td>
                  <td className={`${td} amount text-right font-semibold`}>{p.total_bookings}</td>
                  <td className={`${td} amount text-right`}>{inr(p.total_freight)}</td>
                  {fin && <td className={`${td} amount text-right font-semibold ${p.pending > 0.5 ? "text-rose-600" : p.pending < -0.5 ? "text-emerald-600" : "text-muted-foreground"}`} data-testid={`party-pending-${p.id}`}>
                    {p.pending < -0.5 ? `${inr(-p.pending)} Adv` : inr(p.pending || 0)}{p.pending_bills > 0 && <div className="text-[10px] font-normal text-muted-foreground">{p.pending_bills} LR baaki</div>}</td>}
                  <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {["admin", "manager", "accounts"].includes(role) && <button data-testid={`edit-party-${p.id}`} onClick={() => { setEdit(p); setOpen(true); }} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><Pencil className="h-4 w-4" /></button>}
                      {isAdmin && <button data-testid={`delete-party-${p.id}`} onClick={() => del(p)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                      {role !== "operation" && <button data-testid={`party-profile-${p.id}`} onClick={() => navigate(`/parties/${p.id}`)} className="flex items-center text-xs font-semibold text-primary">Profile <ChevronRight className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      <PartyDialog open={open} onClose={() => setOpen(false)} initial={edit} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
